import axios from "axios";
import { useNotify } from "../hooks/use-notify";
import { useSingleton } from "../hooks/use-singleton";
import { COOKIE_ACCESS_MAIL, COOKIE_ACCESS_ROLE } from "../utils/storage-constants";

export const extractDetailMessage = (detail, defaultFallback = "An unexpected error occurred.") => {
  if (!detail) return defaultFallback;

  if (typeof detail === "string") {
    return detail;
  }

  if (typeof detail === "object" && !Array.isArray(detail)) {
    let msg = detail.message || detail.error || detail.msg || "";
    if (Array.isArray(detail.invalid_questions) && detail.invalid_questions.length > 0) {
      const qDetails = detail.invalid_questions
        .map((iq) => {
          const qId = iq.question_id || iq.id || "";
          const reasons = Array.isArray(iq.reasons) ? iq.reasons.join(" ") : "";
          return qId ? `${qId}: ${reasons}` : reasons;
        })
        .filter(Boolean)
        .join("; ");

      if (qDetails) {
        msg = msg ? `${msg} (${qDetails})` : qDetails;
      }
    }
    if (msg) return msg;
    try {
      return JSON.stringify(detail);
    } catch (e) {
      return defaultFallback;
    }
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => (typeof d === "string" ? d : d?.msg || d?.message || ""))
      .filter(Boolean);
    if (messages.length > 0) return messages.join(", ");
  }

  return defaultFallback;
};

const isWarningDetail = (msg) => {
  if (!msg || typeof msg !== "string") return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes("hasn't started yet") ||
    lower.includes("has not started yet") ||
    lower.includes("not started yet") ||
    lower.includes("has not started") ||
    lower.includes("come back at") ||
    lower.includes("come back") ||
    lower.includes("test hasn't started") ||
    lower.includes("test has not started") ||
    lower.includes("test has ended") ||
    lower.includes("test already ended") ||
    lower.includes("already submitted") ||
    lower.includes("expired")
  );
};

const useAxiosInstance = () => {
  const { toggleNotify } = useNotify();
  const { updateSubscriptionLimitModal } = useSingleton();

  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    timeout: 400000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // ✅ Request interceptor
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // ✅ Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const rawDetail = error?.response?.data?.detail;
      const detailMsg = extractDetailMessage(
        rawDetail,
        error?.message || "An unexpected error occurred."
      );
      const status = error?.response?.status;

      // ✅ Check limit error first — show modal, skip toast
      if (
        typeof rawDetail === "string" &&
        (rawDetail === "You've reached your free question limit. Please purchase a subscription to continue!" ||
          rawDetail === "You've reached your free question limit. Please login to continue!" ||
          rawDetail.toLowerCase().includes("limit"))
      ) {
        updateSubscriptionLimitModal(true);
        return Promise.reject(error);
      }

      // ✅ Warning messages (e.g. test hasn't started yet, come back at...)
      if (isWarningDetail(detailMsg) || (typeof rawDetail === "string" && isWarningDetail(rawDetail))) {
        toggleNotify({
          type: "warning",
          title: "Warning",
          description: detailMsg,
        });
        return Promise.reject(error);
      }

      // ✅ 401 Unauthorized / Expired Session
      if (status === 401) {
        const isAuthPage =
          typeof window !== "undefined" &&
          (window.location.pathname === "/" ||
            window.location.pathname.includes("/sign-in") ||
            window.location.pathname.includes("/login"));

        if (!isAuthPage) {
          localStorage.removeItem("token");
          localStorage.removeItem("access_token");
          localStorage.removeItem(COOKIE_ACCESS_ROLE);
          localStorage.removeItem(COOKIE_ACCESS_MAIL);

          toggleNotify({
            type: "error",
            title: "Session Expired",
            description: detailMsg || "Your session has expired. Please sign in again.",
          });

          setTimeout(() => {
            if (typeof window !== "undefined") {
              window.location.href = "/";
            }
          }, 1200);
          return Promise.reject(error);
        } else {
          toggleNotify({
            type: "error",
            title: "Authentication Failed",
            description: detailMsg || "Invalid credentials. Please try again.",
          });
          return Promise.reject(error);
        }
      }

      // ✅ Standard errors (400, 403, 404)
      if (status === 400 || status === 403 || status === 404) {
        toggleNotify({
          type: "error",
          title: "Error",
          description: detailMsg,
        });
      } else if (status === 422) {
        // ✅ Validation errors (422) — backend detail response
        toggleNotify({
          type: "error",
          title: "Validation Error",
          description: detailMsg,
        });
      } else if (status === 500) {
        // ✅ Server errors (500) — backend detail response
        toggleNotify({
          type: "error",
          title: "Server Error",
          description: detailMsg,
        });
      } else if (status >= 400) {
        toggleNotify({
          type: "error",
          title: "Error",
          description: detailMsg,
        });
      }

      return Promise.reject(error);
    },
  );

  return { axiosInstance };
};

export default useAxiosInstance;